import { component } from "picoapp";
import Cookies from "js-cookie";

const PROMOTION_COOKIE_NAME = "promotion_cookie";
const CLOSED_PROMOTION_BANNERS_COOKIE_NAME = "closed_promotion_banners";

const toggleBanner = (bannerElement: HTMLElement) => {
  bannerElement.classList.toggle("tw-hidden");
};

function setPersistentDiscountCookie({
  discount,
  url,
}: {
  discount: string;
  url: string;
}) {
  const cookieDetails = {
    discount,
    url,
  };
  Cookies.set(PROMOTION_COOKIE_NAME, JSON.stringify(cookieDetails), {
    expires: 7,
  });
}

/**
 * Retrieve all store discounts. Discounts in Shopify are normally a Checkout entity so querying discounts is not something that's provided.
 * To retrieve store discounts we're using a combination of methods:
 * 1. Using cart and line item discount applications in liquid
 * 2. Using a shopify made cookie "discount_code" - this is often tied with shopify discount permalinks
 * 3. Reading url_parameter "discount" - this is leveraged by Rebuy to apply a checkout discount
 * 4. Reading Rebuy SmartCart & Cart discounts
 */

const getAllStoreDiscounts = (node: HTMLElement) => {
  const storeDiscounts = node.dataset.storeDiscounts
    ? JSON.parse(node.dataset.storeDiscounts)
    : [];
  const discountCodeCookie = Cookies.get("discount_code");

  const rebuySmartCartDiscountCode = window.Rebuy?.SmartCart?.discount?.code;
  const rebuyCartDiscountCode = window.Rebuy?.Cart?.discount;

  if (
    discountCodeCookie &&
    !storeDiscounts.find(
      (discountCode: string) =>
        discountCode.toLowerCase() === discountCodeCookie.toLowerCase()
    )
  ) {
    storeDiscounts.push(discountCodeCookie);
  }

  const currentURL = new URL(window.location.href);
  const currentURLSearchParams = new URLSearchParams(currentURL.search);
  const rebuyDiscount = currentURLSearchParams.get("discount");

  if (
    rebuyDiscount &&
    !storeDiscounts.find(
      (discountCode: string) =>
        discountCode.toLowerCase() === rebuyDiscount.toLowerCase()
    )
  ) {
    storeDiscounts.push(rebuyDiscount);
  }

  if (
    rebuySmartCartDiscountCode &&
    !storeDiscounts.find(
      (discountCode: string) =>
        discountCode.toLowerCase() === rebuySmartCartDiscountCode.toLowerCase()
    )
  ) {
    storeDiscounts.push(rebuySmartCartDiscountCode);
  }

  // For Cart template
  if (
    rebuyCartDiscountCode &&
    !storeDiscounts.find(
      (discountCode: string) =>
        discountCode.toLowerCase() === rebuyCartDiscountCode.toLowerCase()
    )
  ) {
    storeDiscounts.push(rebuyCartDiscountCode);
  }

  return storeDiscounts;
};

const getPromotionCookie = () => {
  const promotionCookie = Cookies.get(PROMOTION_COOKIE_NAME);
  return promotionCookie ? JSON.parse(promotionCookie) : {};
};

export const promotionBanner = component((node: HTMLElement) => {
  const initPromotionBanner = () => {
    const closeIcon = node.querySelectorAll<HTMLElement>("[data-icon-close]");
    const promotionBanners = node.querySelectorAll<HTMLElement>(
      "[data-promotion-banner]"
    );
    const currentURL = new URL(window.location.href);

    const storeDiscounts = getAllStoreDiscounts(node);

    // Clean any cookies if discounts were removed
    if (
      getPromotionCookie() &&
      getPromotionCookie().discount &&
      !storeDiscounts.find(
        (discountCode: string) =>
          discountCode.toLowerCase() ===
          getPromotionCookie().discount.toLowerCase()
      )
    ) {
      Cookies.remove(PROMOTION_COOKIE_NAME);
    }

    const closedPromotionBanners = Cookies.get(
      CLOSED_PROMOTION_BANNERS_COOKIE_NAME
    );
    const closedPromotions = closedPromotionBanners
      ? JSON.parse(closedPromotionBanners)
      : [];

    for (let i = 0; i < promotionBanners.length; i++) {
      const bannerElement = promotionBanners[i];
      const promotionDiscount = bannerElement.dataset.promotionDiscount;
      const promotionURL = bannerElement.dataset.promotionUrl
        ? new URL(bannerElement.dataset.promotionUrl).toString()
        : "";

      // Check if banner is in closed promotions (blacklist)
      if (
        closedPromotions.find((closedPromotion) => {
          if (
            closedPromotion.url === promotionURL &&
            closedPromotion.discount === promotionDiscount
          ) {
            return true;
          }

          return false;
        })
      ) {
        continue;
      }

      if (promotionDiscount && promotionURL) {
        if (
          currentURL.toString() === promotionURL &&
          storeDiscounts.find(
            (discountCode: string) =>
              discountCode.toLowerCase() ===
              bannerElement.dataset.promotionDiscount.toLowerCase()
          )
        ) {
          setPersistentDiscountCookie({
            discount: promotionDiscount,
            url: promotionURL,
          });
        }
      } else if (currentURL.toString() === promotionURL) {
        // Set cookie based on url
        setPersistentDiscountCookie({
          discount: promotionDiscount,
          url: promotionURL,
        });
      } else if (
        // Set cookie based on discount
        storeDiscounts.find(
          (discountCode: string) =>
            discountCode.toLowerCase() ===
            bannerElement.dataset.promotionDiscount.toLowerCase()
        )
      ) {
        setPersistentDiscountCookie({
          discount: promotionDiscount,
          url: promotionURL,
        });
      }
    }

    // Toggle display of active banner based on current cookie
    const activeBanner = [...promotionBanners].find((bannerElement) => {
      const promotionCookie = getPromotionCookie();
      const promotionURL = bannerElement.dataset.promotionUrl
        ? new URL(bannerElement.dataset.promotionUrl).toString()
        : "";
      const promotionDiscount = bannerElement.dataset.promotionDiscount;

      return (
        promotionURL === promotionCookie.url &&
        promotionDiscount === promotionCookie.discount
      );
    });

    if (activeBanner) toggleBanner(activeBanner);

    // Closing banner and creating blacklist of promotions
    if (closeIcon) {
      closeIcon.forEach((closeElement) => {
        closeElement.addEventListener("click", () => {
          const bannerElement = closeElement.closest<HTMLElement>(
            "[data-promotion-banner]"
          );
          const promotionCookie = getPromotionCookie();
          Cookies.remove(PROMOTION_COOKIE_NAME);
          toggleBanner(bannerElement);

          const bannedPromotions = Cookies.get(
            CLOSED_PROMOTION_BANNERS_COOKIE_NAME
          );

          const bannedPromotionDetails = bannedPromotions
            ? JSON.parse(bannedPromotions)
            : [];
          bannedPromotionDetails.push(promotionCookie);

          Cookies.set(
            CLOSED_PROMOTION_BANNERS_COOKIE_NAME,
            JSON.stringify(bannedPromotionDetails),
            {
              expires: 7,
            }
          );
        });
      });
    }
  };

  if (window.location.pathname === "/cart") {
    document.addEventListener("rebuy:cart.ready", () => {
      initPromotionBanner();
    });
  } else {
    document.addEventListener("rebuy:smartcart.ready", () => {
      initPromotionBanner();
    });
  }
});
